import React, { useMemo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Row, Card, CardBody, Alert } from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { CustomTable } from "@components/custom-table/table";
import DeleteModal from "@components/custom-table/DeleteModal";
import { resetDelete } from "@helpers/custom-delete/controller/delete-slice";
import { url } from "@constants/url-constants";
import {
  resetError,
  resetState,
  resetSuccess,
} from "../controller/content-slice";
import { getContentList } from "../services/content-services";

export const Contents = ({ mediaId, mediaCurrentpage, type }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const contents = useSelector((state) => state.content.contents);
  const pagination = useSelector((state) => state.content.pagination);
  const isLoading = useSelector((state) => state.content.loading);
  const responseMessage = useSelector((state) => state.content.responseMessage);
  const successMessage = useSelector((state) => state.content.successMessage);
  const deleteMessage = useSelector((state) => state.delete.successMessage);
  const currentPage = pagination?.current_page;
  const deleteUrl = url.contents;
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const pageQuery = searchParams.get("current_page");
  const [deleteModel, setDeleteModel] = useState(false);
  const [deleteData, setDeleteData] = useState({});
  const [visible, setVisible] = useState(false);
  const [content, setContent] = useState("");

  const onDismiss = () => setVisible(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
    }, 5000); // 10000 milliseconds = 10 seconds
    // dispatch(resetSuccess());
    // Cleanup the timer if the component unmounts before the time is up
    return () => clearTimeout(timer);
  }, [deleteMessage, successMessage]);

  useEffect(() => {
    if (responseMessage !== null) {
      dispatch(resetState());
    }
  }, [dispatch, responseMessage]);

  useEffect(() => {
    if (deleteMessage) {
      if (mediaId && mediaCurrentpage && currentPage) {
        setContent(deleteMessage);
        navigate(
          `/medias/edit/${mediaId}?current_page=${mediaCurrentpage}&&content_page=${currentPage}`
        );
      }

      dispatch(resetDelete());
      setVisible(true);
    }
  }, [dispatch, deleteMessage]);

  useEffect(() => {
    if (successMessage !== null) {
      setContent(successMessage);
      setVisible(true);
      const timer = setTimeout(() => {
        dispatch(resetSuccess());
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [dispatch, successMessage]);

  const handleDeleteContent = ({ id, url, deleted_at }) => {
    setDeleteModel(true);
    setDeleteData({ id, url, deleted_at });
  };

  const handleEditContent = ({ id, page, mediaId, currentPage }) => {
    if (!mediaId) {
      navigate(`/contents/edit/${id}?content_page=${page}`);
      dispatch(resetError());
    } else {
      navigate(
        `/contents/edit/${id}?current_page=${currentPage}&content_page=${page}&id=${mediaId}`
      );
      dispatch(resetError());
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "Sorting ",
        accessorKey: "sorting",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Contents",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Duration",
        accessorKey: "duration",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps) => {
          let duration = cellProps.row.original?.duration || 0;
          let hours = Math.floor(duration / 3600);
          let minutes = Math.floor((duration % 3600) / 60);
          let seconds = duration % 60;
          let formattedDuration = hours > 0
            ? `${hours} Hours ${minutes} Minutes ${seconds} Seconds`
            : minutes > 0 ? `${minutes} Minutes ${seconds} Seconds` : ` ${seconds} Seconds`;
          return formattedDuration;
        }
      },
      {
        header: "Size",
        accessorKey: "",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps) => {
          let size = cellProps.row.original?.media?.size || 0;
          let formattedSize;
          if (size >= 1024 * 1024) {
            formattedSize = `${(size / (1024 * 1024)).toFixed(2)} MB`;
          } else if (size >= 1024) {
            formattedSize = `${(size / 1024).toFixed(2)} KB`;
          } else {
            formattedSize = `${size} Bytes`;
          }
          return formattedSize;
        }

      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps) => {
          return (
            <ul className="list-unstyled hstack gap-2  d-flex justify-content-center mb-0">
              <li data-bs-toggle="tooltip" data-bs-placement="top" title="Edit">
                <button
                  type="button"
                  className="btn btn-sm btn-soft-info"
                  onClick={() => {
                    if (cellProps.row.original.deleted_at === null) {
                      handleEditContent({
                        id: cellProps.row.original._id,
                        page: currentPage,
                        mediaId: mediaId,
                        currentPage: mediaCurrentpage,
                      });
                    } else {
                      toast.error("Record Not found");
                    }
                  }}
                  id={`edit-tooltip-${cellProps.row.original.id}`}
                >
                  <i className="mdi mdi-pencil" />
                </button>
              </li>

              <li
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title={
                  cellProps.row.original.deleted_at === null
                    ? "Delete"
                    : "Restore"
                }
              >
                <button
                  type="button"
                  className={`btn btn-sm ${cellProps.row.original.deleted_at === null
                    ? "btn btn-sm btn-soft-danger"
                    : "btn-soft-success"
                    }`}
                  // onClick={() => dispatch(deleteCountry(cellProps.row.original._id))}
                  onClick={() => {
                    handleDeleteContent({
                      id: cellProps.row.original._id,
                      url: deleteUrl,
                      deleted_at: cellProps.row.original.deleted_at,
                    });
                  }}
                  id={`delete-tooltip-${cellProps.row.original.id}`}
                >
                  <i className="mdi mdi-delete-outline" />
                </button>
              </li>
            </ul>
          );
        },
      },
    ],
    [currentPage, deleteUrl]
  );
  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModel}
        onDeleteClick={handleDeleteContent}
        onCloseClick={() => setDeleteModel(false)}
        data={deleteData}
      />
      <div>
        <div className="container-fluid text-center">
          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="">
                  <div className="row">
                    <div className="text-start col-8">
                      <h2>
                        {type === 1 ? "Episode List" : "Chapter List"}
                      </h2>
                    </div>
                    <div className="col-4">
                      <Alert
                        color="success"
                        isOpen={visible}
                        toggle={onDismiss}
                        className="sm"
                      >
                        {content ? content : null}
                      </Alert>
                    </div>
                  </div>
                </CardBody>
                <CardBody>
                  <CustomTable
                    loading={isLoading}
                    columns={columns}
                    data={contents || []}
                    isCustomPageSize={true}
                    isGlobalFilter={true}
                    isJobListGlobalFilter={true}
                    isPagination={true}
                    isAddButton={false}
                    page={currentPage}
                    isEpisode={true}
                    paginationCount={pagination?.total_count}
                    handleList={getContentList}
                    pageId={mediaId}
                    mediaCurrentPage={mediaCurrentpage}
                    paginationLimit={pagination}
                    buttonClass="btn btn-success"
                    buttonName="Add"
                    theadClass="thead-light"
                    SearchPlaceholder="Search for ..."
                    tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
                    pagination="pagination"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};
